import { FxRenderLayer } from "./core/runtime/fx_render_layer.js";
import { InstanceBatch } from "./core/batching/instance_batch.js";
import { IRenderLayerStats } from "./core/types/webgl_types.js";
import { VfxTaskRunner } from "./core/runtime/vfx_task_runner.js";
import { VfxParticleNormalBatch, VfxParticleAdditiveBatch } from "./vfx/particle/vfx_particle_batch.js";
import { VfxConfettiBatch } from "./vfx/confetti/vfx_confetti_batch.js";
import { VfxCoinFlyBatch } from "./vfx/coins/vfx_coin_fly_batch.js";
import { GenericSpriteBatch } from "./core/batching/generic_sprite_batch.js";
import { Vfx2DGlobalAtlas } from "./core/rendering/textures/vfx_2d_global_atlas.js";
import { VFX_2D_ATLAS_CONFIG } from "./vfx_2d_atlas_config.js";
import { Vector2 } from "../common/vector2.js";
import { MoveDirection } from "../common/types.js";
import { VfxCoinFly } from "./vfx/coins/vfx_coin_fly.js";
import { VfxConfettiSide } from "./vfx/confetti/vfx_confetti_side.js";
import { VfxConfettiBottom } from "./vfx/confetti/vfx_confetti_bottom.js";
import { VfxConfettiPoint } from "./vfx/confetti/vfx_confetti_point.js";
import { FireworksColor } from "./vfx/fireworks/fireworks_types.js";
import { VfxFireworks_Explosion } from "./vfx/fireworks/vfx_fireworks_explosion.js";
import { VfxFireworks_Trail } from "./vfx/fireworks/vfx_fireworks_trail.js";
import { VfxFireworks_Sparks } from "./vfx/fireworks/vfx_fireworks_sparks.js";
import { VfxFireworks_BigSparks } from "./vfx/fireworks/vfx_fireworks_big_sparks.js";
import { VfxFireworksBatch } from "./vfx/fireworks/vfx_fireworks_batch.js";
import { VfxUnlockChainOfferBatch } from "./vfx/unlock_chain_offer_item/vfx_unlock_chain_offer_item_spark_batch.js";
import { VfxUnlockChainOfferTask } from "./vfx/unlock_chain_offer_item/vfx_unlock_chain_offer_item_task.js";

export interface IVfxRuntimeStats
{
  main_back_layer: IRenderLayerStats;
  main_front_layer: IRenderLayerStats;
  overlay_back: IRenderLayerStats;
  overlay_front: IRenderLayerStats;
}

export class Vfx2DRuntime
{
  private static main_back_layer: FxRenderLayer;
  private static main_front_layer: FxRenderLayer;
  private static overlay_back_layer: FxRenderLayer;
  private static overlay_front_layer: FxRenderLayer;

  public static onMainBack<T extends InstanceBatch>( ctor: new ( ...args: any[] ) => T ): T
  {
    return this.main_back_layer.get( ctor );
  }

  public static onMainFront<T extends InstanceBatch>( ctor: new ( ...args: any[] ) => T ): T
  {
    return this.main_front_layer.get( ctor );
  }

  public static onOverlayBack<T extends InstanceBatch>( ctor: new ( ...args: any[] ) => T ): T
  {
    return this.overlay_back_layer.get( ctor );
  }

  public static onOverlayFront<T extends InstanceBatch>( ctor: new ( ...args: any[] ) => T ): T
  {
    return this.overlay_front_layer.get( ctor );
  }

  public static init(
    main_back_canvas: HTMLCanvasElement,
    main_front_canvas: HTMLCanvasElement,
    overlay_back_canvas: HTMLCanvasElement,
    overlay_front_canvas: HTMLCanvasElement,
  ): void
  {
    this.main_back_layer = new FxRenderLayer( main_back_canvas );
    this.main_front_layer = new FxRenderLayer( main_front_canvas );
    this.overlay_back_layer = new FxRenderLayer( overlay_back_canvas );
    this.overlay_front_layer = new FxRenderLayer( overlay_front_canvas );
  }

  public static handleCanvasResize(): void
  {
    this.main_back_layer?.handleCanvasResize();
    this.main_front_layer?.handleCanvasResize();
    this.overlay_back_layer?.handleCanvasResize();
    this.overlay_front_layer?.handleCanvasResize();
  }

  public static async setup(): Promise<void>
  {
    this.registerVfx();
    this.main_back_layer.warmup();
    this.main_front_layer.warmup();
    this.overlay_back_layer.warmup();
    this.overlay_front_layer.warmup();

    // CPU JIT Warmup для всіх типів ефектів (щоб JS-рушій завчасно скомпілював їх математику)
    const dummy_pos = new Vector2( -1000, -1000 );

    // 1. Монетки (Coins)
    const coin_batch = this.overlay_front_layer.get( VfxCoinFlyBatch );
    VfxCoinFly.burst( coin_batch, dummy_pos, dummy_pos, 1, -10000 );

    // 2. Конфетті (Confetti)
    const confetti_batch = this.overlay_back_layer.get( VfxConfettiBatch );
    VfxConfettiSide.burst( confetti_batch, -10000 );
    VfxConfettiBottom.burst( confetti_batch, -10000 );
    const confetti_front_batch = this.main_front_layer.get( VfxConfettiBatch );
    VfxConfettiPoint.burst( confetti_front_batch, dummy_pos, -10000 );

    // 3. Феєрверки (Fireworks)
    const fireworks_batch = this.overlay_back_layer.get( VfxFireworksBatch );
    VfxFireworks_Explosion.spawn( fireworks_batch, dummy_pos, FireworksColor.Red, -10000 );
    VfxFireworks_Trail.spawn( fireworks_batch, dummy_pos, dummy_pos, FireworksColor.Red, MoveDirection.Forward, -10000 );
    VfxFireworks_Sparks.spawn( fireworks_batch, dummy_pos, dummy_pos, FireworksColor.Red, MoveDirection.Forward, -10000 );
    VfxFireworks_BigSparks.spawn( fireworks_batch, dummy_pos, dummy_pos, FireworksColor.Red, MoveDirection.Forward, -10000 );

    // 4. Spark Waves
    const spark_ring = new VfxUnlockChainOfferTask( dummy_pos, -10000 );
    spark_ring.onUpdate( -10000 );

    // Очищаємо завершені фейкові таски після прогріву
    VfxTaskRunner.clear();
  }

  private static registerVfx(): void
  {
    // Main Back layer
    this.main_back_layer.register( new GenericSpriteBatch() );

    // Main Front layer
    this.main_front_layer.register( new GenericSpriteBatch() );
    this.main_front_layer.register( new VfxUnlockChainOfferBatch() );
    this.main_front_layer.register( new VfxConfettiBatch() );

    // Overlay Back layer
    this.overlay_back_layer.register( new VfxParticleNormalBatch() );
    this.overlay_back_layer.register( new VfxParticleAdditiveBatch() );
    this.overlay_back_layer.register( new VfxConfettiBatch() );
    this.overlay_back_layer.register( new GenericSpriteBatch() );
    this.overlay_back_layer.register( new VfxFireworksBatch() );

    // Overlay Front layer
    this.overlay_front_layer.register( new VfxParticleNormalBatch() );
    this.overlay_front_layer.register( new VfxParticleAdditiveBatch() );
    this.overlay_front_layer.register( new VfxCoinFlyBatch() );
    this.overlay_front_layer.register( new VfxConfettiBatch() );
    this.overlay_front_layer.register( new GenericSpriteBatch() );
  }

  public static update( now: number ): void
  {
    VfxTaskRunner.update( now );
    this.main_back_layer.update( now );
    this.main_front_layer.update( now );
    this.overlay_back_layer.update( now );
    this.overlay_front_layer.update( now );
  }

  public static renderMainBack( dpr: number ): void
  {
    this.main_back_layer.render( dpr );
  }

  public static renderMainFront( dpr: number ): void
  {
    this.main_front_layer.render( dpr );
  }

  public static renderOverlayBack( dpr: number ): void
  {
    this.overlay_back_layer.render( dpr );
  }

  public static renderOverlayFront( dpr: number ): void
  {
    this.overlay_front_layer.render( dpr );
  }

  public static hasActiveEffects(): boolean
  {
    if ( !this.main_back_layer )
      return VfxTaskRunner.hasActiveTasks();

    return (
      VfxTaskRunner.hasActiveTasks() ||
      this.main_back_layer.hasActiveEffects() ||
      this.main_front_layer.hasActiveEffects() ||
      this.overlay_back_layer.hasActiveEffects() ||
      this.overlay_front_layer.hasActiveEffects()
    );
  }

  public static hasMainBackEffects(): boolean
  {
    return this.main_back_layer.hasActiveEffects();
  }

  public static hasMainFrontEffects(): boolean
  {
    return this.main_front_layer.hasActiveEffects();
  }

  public static hasOverlayBackEffects(): boolean
  {
    return this.overlay_back_layer.hasActiveEffects();
  }

  public static hasOverlayFrontEffects(): boolean
  {
    return this.overlay_front_layer.hasActiveEffects();
  }

  public static getStats(): IVfxRuntimeStats
  {
    return {
      main_back_layer: this.main_back_layer.getStats(),
      main_front_layer: this.main_front_layer.getStats(),
      overlay_back: this.overlay_back_layer.getStats(),
      overlay_front: this.overlay_front_layer.getStats(),
    };
  }

  public static getDetailedStats(): Record<string, Record<string, { active: number; pending: number; emitters: number }>>
  {
    return {
      game_back: this.main_back_layer.getDetailedStats(),
      game_front: this.main_front_layer.getDetailedStats(),
      overlay_back: this.overlay_back_layer.getDetailedStats(),
      overlay_front: this.overlay_front_layer.getDetailedStats(),
    };
  }

  public static clearAll(): void
  {
    VfxTaskRunner.clear();
    this.main_back_layer.clearAll();
    this.main_front_layer.clearAll();
    this.overlay_back_layer.clearAll();
    this.overlay_front_layer.clearAll();
  }

  public static dispose(): void
  {
    VfxTaskRunner.clear();
    this.main_back_layer.dispose();
    this.main_front_layer.dispose();
    this.overlay_back_layer.dispose();
    this.overlay_front_layer.dispose();
  }
}
